import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchData } from "../../utils/functions";
import { useDispatch, useSelector } from "react-redux";
import WashInformationPanel from "./WashInformationPanel";
import WashSanitation from "./WashSanitation";
import WashWater from "./WashWater";

const EditWash = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  const [isLeftClicked, setIsLeftClicked] = useState(false);
  const [isRightClicked, setIsRightClicked] = useState(false);
  const [loader, setLoader] = useState(true);
  const [isFetching, setIsFetching] = useState(true);
  const [washInfo, setWashInfo] = useState(null);
  const [washSanitation, setWashSanitation] = useState(null);
  const [washWater, setWashWater] = useState(null);
  const [error, setError] = useState(null);

  const { user: loggedInUser } = useSelector((state) => state.auth);
  const permissions = loggedInUser?.user_permissions || [];

  const getTabsBasedOnWashType = (washInfo) => {
    if (!washInfo || !washInfo.wash_type) {
      return [
        { label: "Wash Information", permission: "wash-information-update" },
        { label: "Wash Water", permission: "wash-water-update" },
        { label: "Wash Sanitation", permission: "wash-sanitation-update" },
      ];
    }

    const baseTabs = [
      { label: "Wash Information", permission: "wash-information-update" },
    ];

    if (washInfo.wash_type === "water") {
      baseTabs.push({
        label: "Wash Water",
        permission: "wash-water-update",
      });
    } else if (washInfo.wash_type === "sanitation") {
      baseTabs.push({
        label: "Wash Sanitation",
        permission: "wash-sanitation-update",
      });
    } else if (washInfo.wash_type === "both") {
      baseTabs.push(
        { label: "Wash Water", permission: "wash-information-update" },
        { label: "Wash Sanitation", permission: "wash-sanitation-update" }
      );
    }

    return baseTabs;
  };

  const tabs = getTabsBasedOnWashType(washInfo);
  const visibleTabs = tabs.filter((tab) =>
    permissions.includes(tab.permission)
  );
  const [activeTab, setActiveTab] = useState("");

  useEffect(() => {
    if (!activeTab && visibleTabs.length > 0) {
      setActiveTab(visibleTabs[0].label);
    }
  }, [visibleTabs, activeTab]);

  const scrollTabs = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === "left" ? -200 : 200;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });

      if (direction === "left") {
        setIsLeftClicked(true);
        setTimeout(() => setIsLeftClicked(false), 200);
      } else {
        setIsRightClicked(true);
        setTimeout(() => setIsRightClicked(false), 200);
      }
    }
  };

  const fetchWashData = async () => {
    if (!id) {
      setError("No wash ID provided");
      setLoader(false);
      setIsFetching(false);
      return;
    }

    try {
      setError(null);

      const response = await fetchData(
        `wash-information/${id}`,
        "GET",
        null,
        {},
        {},
        navigate,
        "/wash",
        dispatch
      );

      const data = response?.data;
      if (data) {
        setWashInfo(data);

        // Fetch related wash water data
        try {
          const waterResponse = await fetchData(
            `wash-water`,
            "GET",
            null,
            { wash_information_id: id },
            {},
            navigate,
            "/wash",
            dispatch
          );

          if (waterResponse?.data?.wash_water) {
            setWashWater(waterResponse.data.wash_water);
          }
        } catch (waterError) {
          console.warn("Failed to fetch wash water data:", waterError);
          setWashWater(null);
        }

        // Fetch related wash sanitation data
        try {
          const sanitationResponse = await fetchData(
            `wash-sanitation`,
            "GET",
            null,
            { wash_information_id: id },
            {},
            navigate,
            "/wash",
            dispatch
          );

          if (sanitationResponse?.data?.wash_sanitation) {
            setWashSanitation(sanitationResponse.data.wash_sanitation);
          }
        } catch (sanitationError) {
          console.warn(
            "Failed to fetch wash sanitation data:",
            sanitationError
          );
          setWashSanitation(null);
        }
      } else {
        setError("No data received from server");
      }
    } catch (error) {
      console.error("Error fetching wash data:", error);
      setError(error.message || "Failed to fetch wash data");
    } finally {
      setLoader(false);
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchWashData();
  }, [id]);

  if (loader || isFetching) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-lg text-gray-600">Loading wash data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-lg text-red-600">Error: {error}</div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-[1.475rem] text-black font-bold">
        Edit Wash Information
      </h1>

      <div className="mt-6">
        <div className="flex items-center rounded-t-lg rounded-tr-lg bg-[#fdf5ec] relative">
          <button
            onClick={() => scrollTabs("left")}
            className={`text-xl px-4 py-2 font-bold rounded-lg transition-colors ${
              isLeftClicked ? "bg-[#caa316] text-white" : "text-black"
            }`}
          >
            «
          </button>

          <div
            ref={scrollRef}
            className="flex gap-4 overflow-hidden scrollbar-hide"
          >
            {visibleTabs.map((tab) => (
              <button
                key={tab.label}
                onClick={() => setActiveTab(tab.label)}
                className={`px-10 py-2 font-semibold whitespace-nowrap ${
                  activeTab === tab.label
                    ? "bg-[#caa316] text-white"
                    : "text-black"
                }`}
                style={{
                  borderTopLeftRadius: "13px",
                  borderTopRightRadius: "30px",
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <button
            onClick={() => scrollTabs("right")}
            className={`ml-auto text-xl px-4 py-2 rounded-lg font-bold transition-colors ${
              isRightClicked ? "bg-[#caa316] text-white" : "text-black"
            }`}
          >
            »
          </button>
        </div>

        <div className="bg-white rounded-b-lg rounded-br-lg shadow-md p-6">
          {activeTab === "Wash Information" && (
            <WashInformationPanel
              washInfo={washInfo}
              onUpdate={() => fetchWashData()}
            />
          )}

          {activeTab === "Wash Water" && (
            <WashWater
              washWater={washWater}
              washInformationId={id}
              onUpdate={() => fetchWashData()}
            />
          )}

          {activeTab === "Wash Sanitation" && (
            <WashSanitation
              washSanitation={washSanitation}
              washInformationId={id}
              onUpdate={() => fetchWashData()}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default EditWash;
